import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft, ArrowRight, CheckCircle } from 'lucide-react';
import { AUTH_URL } from '../contexts/AuthContextMySQL';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${AUTH_URL}/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      const data = await response.json();
      if (!data.success) throw new Error(data.error || 'Une erreur est survenue');
      setMessage(data.message);
    } catch (err) {
      setError(err.message === 'Failed to fetch' ? 'Serveur injoignable, réessayez plus tard' : err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-950 text-white flex items-center justify-center px-4 py-8">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <motion.div 
          className="absolute top-1/4 left-1/4 w-96 h-96 bg-yellow-500/10 rounded-full blur-3xl"
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 8, repeat: Infinity }}
        />
      </div>

      <motion.div 
        className="w-full max-w-md relative z-10"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3">
            <img src="/logo512.png" alt="AV Kamas" className="w-12 h-12 rounded-full shadow-[0_0_20px_rgba(250,204,21,0.4)]" />
            <span className="text-3xl font-bold tracking-wider">
              <span className="text-yellow-400">AV</span>
              <span className="text-white">KAMAS</span>
            </span>
          </Link>
        </div>

        <div className="bg-gray-900/80 backdrop-blur-md border border-gray-800 rounded-2xl p-8">
          <h2 className="text-2xl font-bold text-center mb-2">Mot de passe oublié</h2>

          {message ? (
            <div className="text-center">
              <CheckCircle className="w-12 h-12 text-green-400 mx-auto my-4" />
              <p className="text-gray-300 mb-2">{message}</p>
              <p className="text-gray-500 text-sm mb-6">
                Le lien est valable 60 minutes. Pensez à vérifier vos spams.
              </p>
              <Link to="/login" className="text-yellow-400 hover:text-yellow-300 font-semibold">
                Retour à la connexion
              </Link>
            </div>
          ) : (
            <>
              <p className="text-gray-400 text-center mb-6">
                Entrez votre email, nous vous enverrons un lien pour choisir un nouveau mot de passe.
              </p>

              {error && (
                <div className="bg-red-500/20 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg mb-4 text-sm">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={20} />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => { setEmail(e.target.value); setError(''); }}
                      required
                      className="w-full bg-gray-950 border border-gray-700 rounded-lg py-3 pl-10 pr-4 text-white placeholder-gray-500 focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 outline-none transition-all"
                      placeholder="votre@email.com"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-400 hover:to-yellow-500 text-black font-bold py-3 rounded-lg flex items-center justify-center gap-2 transition-all disabled:opacity-50"
                >
                  {loading ? 'Envoi...' : <>Envoyer le lien <ArrowRight size={18} /></>}
                </button>
              </form>

              <div className="mt-6 text-center">
                <Link to="/login" className="inline-flex items-center gap-2 text-gray-400 hover:text-white">
                  <ArrowLeft size={16} /> Retour à la connexion
                </Link>
              </div>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}
